import { ArrowRightOutlined } from '@ant-design/icons'
import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type { LogCategory, LogCategoryItem } from '@lmc/ts-common'
import { Heading, MultiSelect } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Card, Divider } from 'antd'
import useSafeguardingAccess from 'components/AccessControl/useSafeguardingAccess'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

const AUTO_LOG_LINKING_PRODUCT_BOARD =
  'https://portal.productboard.com/ambpva5y3bxf5myjgnmwwnpg/c/66-request-new-log-recording-options?&utm_medium=social&utm_source=starter_share'
const unwantedCategories = ['bodyMaps']

type CategoriesResult = {
  [categoryId: string]: string[]
}

interface CategoryMultiSelectProps {
  items: LogCategoryItem[]
  onChange: (selectedCategories: CategoriesResult) => void
  color?: string
  category: string
  logsLinked: string[]
}

const CategoryMultiSelect = ({
  items,
  color,
  onChange,
  category,
  logsLinked
}: CategoryMultiSelectProps) => {
  const categoryOptions = items
    .map((item) => ({
      label: item.name,
      value: item.id,
      image: (
        <CategoryImage
          src={String(item.icon)}
          alt="category"
          color={color}
          size={20}
        />
      )
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <StyledMultiSelect
      values={logsLinked}
      title="Items"
      options={categoryOptions || []}
      hasClear
      hasSearch
      hasSelectAll
      onChange={(selectedCategories) => {
        onChange({ [category]: selectedCategories })
      }}
    />
  )
}

interface AutomaticLogLinkingProps {
  setLogsLinked: React.Dispatch<React.SetStateAction<CategoriesResult>>
  logsLinked?: CategoriesResult
  testId?: string
}

const AutomaticLogLinking = ({
  setLogsLinked,
  logsLinked = {},
  testId
}: AutomaticLogLinkingProps) => {
  const { t } = useTranslation(NAMESPACE.GOALS)
  const baseId = testId || 'automatic-log-linking'

  const {
    'log-categories': categories,
    'log-category-items': categoryItems
  }: {
    'log-categories': LogCategory[] | undefined
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector(
    (state: {
      data: {
        'log-category-items': LogCategoryItem[]
        'log-categories': LogCategory[]
      }
    }) => state.data
  )

  const safeguardingAccess = useSafeguardingAccess()

  const filteredCategories = categories.filter(
    (category) => !unwantedCategories.includes(category.itemId)
  )

  const filteredCategoryItems = !safeguardingAccess
    ? categoryItems.filter((item) => item.id !== SAFEGUARDING_ITEM_ID)
    : categoryItems

  return (
    <Card data-testid={baseId}>
      <HeadingBlock level={3}>
        {t(goalsConstants.AUTOMATIC_LOG_LINKING)}
      </HeadingBlock>
      <StyledDivider />
      <StyledHeading level={5} weight="medium">
        {t(goalsConstants.LOG_LINKING_DESCRIPTION)}
      </StyledHeading>
      <a href={AUTO_LOG_LINKING_PRODUCT_BOARD} rel="noreferrer" target="_blank">
        <Button
          variant="text"
          icon={<ArrowRightOutlined />}
          style={{ marginTop: '10px' }}
          data-testid={`${baseId}-request-btn`}
        >
          {t(goalsConstants.REQUEST_LOG_TYPES)}
        </Button>
      </a>
      <CategoriesWrapper data-testid={`${baseId}-categories`}>
        {filteredCategories.map((category) => (
          <CategoryItem key={category.id}>
            <CategoryTitleWrapper>
              <CategoryImage
                src={String(category.icon)}
                alt="category"
                color={category.color}
                size={40}
              />
              <SyledText level={1} key={category.id}>
                {`${category.name}:`}
              </SyledText>
            </CategoryTitleWrapper>
            <CategoryMultiSelect
              items={filteredCategoryItems.filter(
                (item) => item.category === category.id
              )}
              color={category.color}
              category={category.id}
              onChange={(values) =>
                setLogsLinked((prev: any) => ({ ...prev, ...values }))
              }
              logsLinked={logsLinked[category.id]}
            />
          </CategoryItem>
        ))}
      </CategoriesWrapper>
    </Card>
  )
}

export default AutomaticLogLinking

const CategoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 24px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    justify-content: center;
    align-items: center;
  }
`

const CategoryItem = styled.div`
  width: 66.66%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    gap: 8px;
  }
`

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeadingBlock = styled(Heading)`
  display: block;
  margin-bottom: 4px;
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const SyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const CategoryImage = styled.img<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background: ${({ color }) => color};
  border-radius: ${({ theme }) => theme.properties.borderRadius.small};
  padding: 5px;
`

const StyledMultiSelect = styled(MultiSelect)`
  width: 400px;
  height: 40px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: fit-content;
  }
` as typeof MultiSelect
